/**
 * @file custom-notification.tsx
 * @module app/modules/custom-notification/custom-notification
 * @description Creates custom customer notifications, including recipient and product targeting,
 * promotional settings, image validation, and notification delivery.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Button, Card, Col, Row } from 'react-bootstrap';
import { CustomNotificationString } from '../../../utils/string';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Add, ApprovedProduct, CustomNotifications, PAGE_LIMIT } from '../../../utils/constants';
import { error, success } from '../../../Global/toast';
import APICallService from '../../../api/apiCallService';
import { buyer, dashboard, manageProduct } from '../../../api/apiEndPoints';
import { notificationJSON } from '../../../utils/staticJSON';
import { CustomSelectWhite } from '../../custom/Select/CustomSelectWhite';
import { CustomReportSelect } from '../../custom/Select/CustomReportSelect';
import Method from '../../../utils/methods';
import { useNavigate } from 'react-router-dom';
import e from 'express';
import { useAuth } from '../auth';
import { warehouse as warehouseStoreString } from '../../../utils/storeString';
import { getKey } from '../../../Global/history';
import { useWarehouse } from '../../context/WarehouseContext';
import UploadImage from '../../../_admin/assets/media/svg_uTrade/add-img.svg';
import { fileValidation } from '../../../Global/fileValidation';
import Close from '../../../_admin/assets/media/svg_uTrade/cross-rounded-gray.svg';
import ImageWithFallback from '../_common/components/image-with-fallback';

const CustomNotification = () => {

  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [productLoading, setProductLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [notificationData, setNotificationData] = useState<{
    title: string;
    message: string;
    image: string;
    imageReader: any;
    buyers: any;
    variants: any;
  }>({
    title: "",
    message: "",
    image: "",
    imageReader: "",
    buyers: [],
    variants: [],
  });
  const [validation, setValidation] = useState<{
    title: boolean;
    message: boolean;
    image: boolean;
    buyers: boolean;
    variants: boolean;
  }>({
    title: false,
    message: false,
    image: false,
    buyers: false,
    variants: false,
  });
  const [startDate, setStartDate] = useState<any>(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState<any>(new Date());
  const [customerList, setCustomerList] = useState<any>([]);
  const [productsList, setProductsList] = useState<any>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>([]);
  const [topOrderedData, setTopOrderedData] = useState<any>([]);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [reload, setReload] = useState<Boolean>(false);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sendToAllCustomer, setSendToAllCustomer] = useState(false);
  const [pinPromotion, setPinPromotion] = useState(false);
  const hasViewPermission = !Method.hasPermission(
    CustomNotifications,
    Add,
    currentUser
  );
  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(CustomNotifications, currentUser)) {
        return window.history.back();
      }
      await fetchCustomers(1, PAGE_LIMIT);
    })();
  }, []);
  const fetchCustomers = async (
    pageNo: number,
    limit: number = 10,
    searchTerm: string = ""
  ) => {
    setCustomerLoading(true);
    if (pageNo === 1) setCustomerList([]);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      state: 1,
      needCount: true,
      searchTerm: searchTerm,
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }
    const apiService = new APICallService(buyer.listBuyer, params, '', '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    const response = await apiService.callAPI();
    if (response) {
      setTotalCustomers(response.total);
      // let data: any = [];
      let selectAll: any = [
        {
          value: 0,
          name: "Select All",
          title: "Select All",
          label: (
            <>
              <span className="fs-16 fw-600 text-black ">Select All</span>
            </>
          ),
          id: 0,
        },
      ];

      // data.unshift(...selectAll);
      setCustomerList((preData: any) => {
        let data: any = [];
        response.records.map((val: any) => {
          data.push({
            value: val._id,
            name: val.business.name,
            title: val.business.name,
            label: (
              <>
                <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                  <ImageWithFallback src={val.business.image} className="p-1" noDefaultSize />
                </span>
                <span className="fs-16 fw-600 text-black ">
                  {val.business.name}
                </span>
              </>
            ),
            id: val._id,
          });
        });
        if (pageNo === 1) {
          return data;
        } else {
          return [...preData, ...data];
        }
      });
    }
    setCustomerLoading(false);
  };
  const fetchActiveCustomers = async (
    pageNo: number,
    limit: number = 10,
    searchTerm: string = ""
  ) => {
    setCustomerLoading(true);
    if (pageNo === 1) setCustomerList([]);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      state: 1,
      needCount: true,
      searchTerm: searchTerm,
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }
    const apiService = new APICallService(buyer.activeCustomerList, params, '', '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    const response = await apiService.callAPI();
    if (response) {
      setTotalCustomers(response.total);
      setCustomerList((preData: any) => {
        let data: any = [];
        response.records.map((val: any) => {
          data.push({
            value: val._id,
            name: val.business.name,
            title: val.business.name,
            label: (
              <>
                <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                  <ImageWithFallback src={val.business.image} className="p-1" noDefaultSize />
                </span>
                <span className="fs-16 fw-600 text-black ">
                  {val.business.name}
                </span>
              </>
            ),
            id: val._id,
          });
        });
        if (pageNo === 1) {
          return data;
        } else {
          return [...preData, ...data];
        }
      });
    }
    setCustomerLoading(false);
  };
  const fetchProducts = async (pageNo: number) => {
    setProductLoading(true);
    if (pageNo === 1) setProductsList([]);
    let data: any = {
      pageNo: pageNo,
      limit: 0,
      sortKey: "title",
      sortOrder: -1,
      state: 1,
      needCount: false,
      ["status[0]"]: ApprovedProduct,
    };
    if (selectedWarehouse?.value) {
      data = {
        ...data,
        warehouse: selectedWarehouse?.value,
      };
    }
    let apiService = new APICallService(manageProduct.listVariant, data, '', '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    let response = await apiService.callAPI();
    if (response.records) {
      let data: any = [];
      response.records.map((val: any) => {
        data.push({
          value: val._id,
          name: Method.replaceSpace(val.title),
          title: Method.replaceSpace(val.title),
          label: (
            <>
              <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                <ImageWithFallback src={val.media[0].image} className="p-1" noDefaultSize />
              </span>
              <span className="fs-16 fw-600 text-black ">
                {Method.replaceSpace(val.title)}
              </span>
            </>
          ),
          id: val._id,
          productId: val.product.reference,
        });
      });
      setProductsList(data);
    } else {
      setProductsList([]);
    }
    setProductLoading(false);
  };
  const fetchTopProducts = async () => {
    setProductLoading(true);
    setProductsList([]);
    let params: any = {
      fromDate: Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
      toDate: Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
      utcOffset: new Date().getTimezoneOffset(),
      reportType: "2",
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }
    let apiService = new APICallService(dashboard.orderReport, params, '', '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    let response = await apiService.callAPI();
    if (response && response.topProducts.length) {
      let data: any = [];
      let selectAll: any = [
        {
          value: 0,
          name: "Select All",
          title: "Select All",
          label: (
            <>
              <span className="fs-16 fw-600 text-black ">Select All</span>
            </>
          ),
          id: 0,
        },
      ];
      response.topProducts.map((val: any) => {
        data.push({
          value: val._id,
          name: Method.replaceSpace(val.variant.title),
          title: Method.replaceSpace(val.variant.title),
          label: (
            <>
              <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                <ImageWithFallback src={val.variant.media[0].image} className="p-1" noDefaultSize />
              </span>
              <span className="fs-16 fw-600 text-black ">
                {Method.replaceSpace(val.variant.title)}
              </span>
            </>
          ),
          id: val.variant._id,
          productId: val.variant?.product?.reference,
        });
      });
      data.unshift(...selectAll);
      setProductsList(data);
    } else {
      error("No top products are available for selected customer");
      setProductsList([]);
    }
    setProductLoading(false);
  };
  const fetchTopCustomers = async () => {
    setCustomerLoading(true);
    setCustomerList([]);
    let params: any = {
      fromDate: Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
      toDate: Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
      // fromDate: '2023-11-01',
      // toDate: '2023-11-30',
      // utcOffset: -330,
      utcOffset: new Date().getTimezoneOffset(),
      reportType: "1",
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }
    let apiService = new APICallService(dashboard.orderReport, params, '', '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    let response = await apiService.callAPI();
    if (response && response.topCustomers.length) {
      let data: any = [];
      let selectAll: any = [
        {
          value: 0,
          name: "Select All",
          title: "Select All",
          label: (
            <>
              <span className="fs-16 fw-600 text-black ">Select All</span>
            </>
          ),
          id: 0,
        },
      ];
      response.topCustomers.map((val: any) => {
        data.push({
          value: val.customer._id,
          name: val.customer.business.name,
          title: val.customer.business.name,
          label: (
            <>
              <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                <ImageWithFallback src={val.customer.business.image} className="p-1" noDefaultSize />
              </span>
              <span className="fs-16 fw-600 text-black ">
                {val.customer.business.name}
              </span>
            </>
          ),
          id: val.customer._id,
        });
      });
      data.unshift(...selectAll);
      setCustomerList(data);
    } else {
      error("No top customers in last month");
      setCustomerList([]);
      setProductsList([]);
    }
    setCustomerLoading(false);
  };
  const fetchBuyerCart = async (customerId: string) => {
    setProductLoading(true);
    setProductsList([]);
    let data: any = {
      limit: 0,
      sortKey: "title",
      sortOrder: -1,
      needCount: false,
    };
    if (selectedWarehouse?.value) {
      data = {
        ...data,
        warehouse: selectedWarehouse?.value,
      };
    }
    let apiService = new APICallService(buyer.cart, data, {
      id: customerId,
    }, '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    let response = await apiService.callAPI();
    if (response.records.length) {
      setProductsList(response.records);
    } else {
      error("No products are available in the cart");
      setProductsList([]);
    }
    setProductLoading(false);
  };
  const fetchTopOrders = async (customerId: string) => {
    setProductLoading(true);
    setProductsList([]);
    let params: any = {
      fromDate: Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
      toDate: Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
      // utcOffset: -330,
      utcOffset: new Date().getTimezoneOffset(),
      reportType: "1",
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }
    let apiService = new APICallService(buyer.dashboard, params, {
      id: customerId,
    }, '', '', '', CustomNotifications, selectedWarehouse?.value || null);
    let response = await apiService.callAPI();
    if (response) {
      if (response.topProducts.length) {
        let data: any = [];
        let selectAll: any = [
          {
            value: 0,
            name: "Select All",
            title: "Select All",
            label: (
              <>
                <span className="fs-16 fw-600 text-black ">Select All</span>
              </>
            ),
            id: 0,
          },
        ];
        response.topProducts.map((val: any) => {
          data.push({
            value: val._id,
            name: Method.replaceSpace(val.variant.title),
            title: Method.replaceSpace(val.variant.title),
            label: (
              <>
                <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                  <ImageWithFallback src={val.variant.media[0].image} className="p-1" noDefaultSize />
                </span>
                <span className="fs-16 fw-600 text-black ">
                  {Method.replaceSpace(val.variant.title)}
                </span>
              </>
            ),
            id: val.variant._id,
            productId: val.variant?.product?.reference,
          });
        });
        data.unshift(...selectAll);
        setProductsList(data);
      } else {
        error("No top ordered products are available for these customer");
        setProductsList([]);
      }
    }
    setProductLoading(false);
  };
  const handleInputChange = (event: any, name: string) => {
    const tempData: any = { ...notificationData };
    const tempValidation: any = { ...validation };
    tempData[name] = event.target.value;
    if (event.target.value.trim().length > 0) {
      tempValidation[name] = false;
    } else {
      tempValidation[name] = true;
    }
    setValidation(tempValidation);
    setNotificationData(tempData);
  };
  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    let temp = { ...notificationData };
    let validateTemp = { ...validation };
    if (!selectedFiles) return;
    else {
      if (fileValidation(selectedFiles?.[0])) {
        validateTemp.image = false;
        temp.image = URL.createObjectURL(selectedFiles?.[0]);
        temp.imageReader = selectedFiles?.[0];
        setNotificationData(temp);
      }
    }
    setValidation(validateTemp);
    event.target.value = "";
  };
  const handleImageDiscard = () => {
    let temp = { ...notificationData };
    let validateTemp = { ...validation };
    validateTemp.image = true;
    temp.image = '';
    temp.imageReader = '';
    setNotificationData(temp);
    setValidation(validateTemp);
  };
  const handleCustomer = (event: any) => {
    const tempData = { ...notificationData };
    if (Array.isArray(event)) {
      if (event.length > tempData.buyers.length) {
        if (
          event.some((item) => item.value === 0) ||
          event.length == customerList.length - 1
        ) {
          tempData.buyers = customerList;
        } else {
          tempData.buyers = event;
        }
      } else {
        if (event.some((val: any) => val.value === 0)) {
          let temp = event.filter((val: any) => val.value !== 0);
          tempData.buyers = temp;
        } else if (
          !event.some((val: any) => val.value === 0) &&
          event.length == customerList.length - 1
        ) {
          tempData.buyers = [];
        } else {
          tempData.buyers = event;
        }
      }
    } else {
      tempData.buyers = [event];
    }
    setSelectedCustomer(event);
    setNotificationData(tempData);
    if (event && selectedNotification?.value == 3) {
      fetchBuyerCart(event.id);
    }
    if (event && selectedNotification?.value == 2) {
      if (tempData.buyers.length) {
        fetchTopOrders(event.id);
      }
    }
  };
  const handleProduct = (event: any) => {
    const tempData = { ...notificationData };
    if (Array.isArray(event)) {
      if (event.length > tempData.variants.length) {
        if (
          event.some((item) => item.value === 0) ||
          event.length == productsList.length - 1
        ) {
          tempData.variants = productsList;
        } else {
          tempData.variants = event;
        }
      } else {
        if (event.some((val: any) => val.value === 0)) {
          let temp = event.filter((val: any) => val.value !== 0);
          tempData.variants = temp;
        } else if (
          !event.some((val: any) => val.value === 0) &&
          event.length == productsList.length - 1
        ) {
          tempData.variants = [];
        } else {
          tempData.variants = event;
        }
      }
    } else {
      tempData.variants = [event];
    }
    setSelectedProduct(event);
    setNotificationData(tempData);
  };
  const handleNotification = (event: any) => {
    const temp = { ...notificationData };
    temp.buyers = [];
    temp.variants = [];
    setProductsList([]);

    if (!event) {
      if (selectedNotification && (selectedNotification.value === 6 || selectedNotification.value === 8)) {
        setCustomerList([]);
        fetchCustomers(1, PAGE_LIMIT);
      }
      setSelectedNotification("");
      setNotificationData(temp);
      return;
    }

    if (
      event.value === 6 ||
      event.value === 8 ||
      (selectedNotification && (selectedNotification.value === 6 || selectedNotification.value === 8))
    ) {
      setCustomerList([]);
    }

    if (event.value == 2 || event.value == 3 || event.value == 4 || event.value == 7 || event.value == 8) {
      setSendToAllCustomer(false);
    }

    setSelectedNotification(event);
    setNotificationData(temp);

    if (event.value !== 8) {
      setPinPromotion(false);
    } else {
      setPinPromotion(true);
    }

    if (selectedNotification && (selectedNotification.value === 6 || selectedNotification.value === 8)) {
      fetchCustomers(1, PAGE_LIMIT);
    }

    if (event.value == 4) {
      fetchProducts(1);
    } else if (event.value === 5) {
      fetchTopProducts();
    } else if (event.value === 6) {
      fetchTopCustomers();
    } else if (event.value === 8) {
      fetchActiveCustomers(1, PAGE_LIMIT);
    }
  };
  const handleSubmit = async () => {
    const tempValidation: any = { ...validation };
    const tempData: any = { ...notificationData };
    tempData.title = tempData.title.trim();
    tempData.message = tempData.message.trim();
    if (tempData.title.length === 0) {
      tempValidation.title = true;
    }
    if (tempData.message.length === 0) {
      tempValidation.message = true;
    }
    setValidation(tempValidation);
    if (!tempValidation.title && !tempValidation.message) {
      if (!selectedNotification) {
        return error("Please select a notification type");
      }
      if (
        selectedNotification.value == 2 &&
        selectedNotification.value == 5 &&
        selectedNotification.value == 4 &&
        (tempData.buyers.length === 0 || tempData.variants.length === 0)
      ) {
        return error("Please select customers and products");
      }
      if (
        selectedNotification.value !== 2 &&
        tempData.buyers.length === 0 &&
        selectedNotification.value !== 7 &&
        !sendToAllCustomer
      ) {
        return error("Please select customers");
      }
      if (
        selectedNotification.value !== 3 &&
        tempData.buyers.some((val: any) => val.value == 0)
      ) {
        tempData.buyers.shift();
      }
      if (selectedNotification.value === 3) {
        if (!productsList.length) {
          return error("No products are available in the cart");
        }
      }
      if (
        (selectedNotification.value == 2 ||
          selectedNotification.value == 4 ||
          selectedNotification.value == 5 ||
          selectedNotification.value == 6) &&
        tempData.variants.some((val: any) => val.value == 0)
      ) {
        tempData.variants.shift();
      }
      tempData.buyers = tempData.buyers.map((item: any) => item.value);
      tempData.variants = tempData.variants.map((item: any) => {
        return {
          variantId: item.value,
          productId: item.productId,
        };
      });
      tempData.type = selectedNotification.type;
      setLoading(true);
      if (sendToAllCustomer) {
        tempData.buyers = await fetchAllCustomer();
      }

      let payload = new FormData();
      payload.append("title", tempData.title);
      payload.append("message", tempData.message);
      payload.append("type", tempData.type || "");
      if (tempData.imageReader) {
        payload.append("image", tempData.imageReader);
      }
      for (let i = 0; i < tempData.buyers.length; i++) {
        payload.append(`buyers[${i}]`, tempData.buyers[i]);
      }
      for (let i = 0; i < tempData.variants.length; i++) {
        payload.append(`variants[${i}][variantId]`, tempData.variants[i].variantId);
        payload.append(`variants[${i}][productId]`, tempData.variants[i].productId);
      }

      const apiService = new APICallService(buyer.sendNotification, payload, '', '', '', '', CustomNotifications, selectedWarehouse?.value || null);
      const response = await apiService.callAPI();
      if (response) {
        success("Notification Sent successfully!");
        navigate("/custom-notification");
        setReload(!reload);
        setSendToAllCustomer(false);
      }
      setLoading(false);
    }
  };
  console.log("notificationData", notificationData, selectedNotification);
  useEffect(() => {
    (async () => {
      setPinPromotion(false);
      setNotificationData({
        title: "",
        message: "",
        image: "",
        imageReader: "",
        buyers: [],
        variants: [],
      });
      await fetchCustomers(1);
      setProductsList([]);
      setSelectedProduct([]);
      setSelectedNotification(null);
      setSelectedCustomer([]);
    })();
  }, [reload]);
  const onMenuScrollToBottom = async () => {
    if (!(customerList.length === totalCustomers)) {
      let tempPage = page;
      tempPage = tempPage + 1;
      setPage(tempPage);
      if (selectedNotification && selectedNotification.value === 8) {
        await fetchActiveCustomers(tempPage, PAGE_LIMIT, searchTerm);
      } else if (selectedNotification && selectedNotification.value === 6) {
        // do not fetch for top customers
      } else {
        await fetchCustomers(tempPage, PAGE_LIMIT, searchTerm);
      }
    }
  };
  const onMenuClose = async () => {
    setSearchTerm("");
  };
  const onMenuOpen = async () => {
    setPage(1);
    if (selectedNotification && selectedNotification.value === 8) {
      await fetchActiveCustomers(1, PAGE_LIMIT, searchTerm);
    } else if (selectedNotification && selectedNotification.value === 6) {
      // do not fetch for top customers
    } else {
      await fetchCustomers(1, PAGE_LIMIT, searchTerm);
    }
  };
  const handleSelectInputChange = async (newValue: string) => {
    setPage(1);
    setSearchTerm(newValue);
    if (selectedNotification && selectedNotification.value === 8) {
      await fetchActiveCustomers(1, PAGE_LIMIT, newValue);
    } else if (selectedNotification && selectedNotification.value === 6) {
      // do not fetch for top customers
    } else {
      await fetchCustomers(1, PAGE_LIMIT, newValue);
    }
  };
  const fetchAllCustomer = async () => {
    let params: any = {
      sortKey: "createdAt",
      sortOrder: -1,
      state: 1,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    const apiService = new APICallService(
      selectedNotification?.value === 8 ? buyer.activeCustomerList : buyer.listBuyer,
      params,
      "",
      "",
      "",
      "",
      CustomNotifications,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    let data: any = [];
    if (response) {
      data = response?.records.map((item: any) => {
        return item?._id;
      });
    }
    return data;
  };
  return (
    <div className="p-5">
      <Row className="g-6">
        <Col xs={12}>
          <h1 className="fs-22 fw-bolder mb-0">
            {CustomNotificationString.cutomNofication}
          </h1>
        </Col>
        <Col xs={12}>
          <Card className="bg-light border">
            <Card.Body className="p-5">
              <Row>
                <Col xs={12} className="mb-6 ">
                  <Row className="align-items-center">
                    <Col lg={2}>
                      <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                        {CustomNotificationString.title}
                      </label>
                    </Col>
                    <Col lg={5}>
                      <input
                        type="text"
                        className={clsx(
                          "form-control form-control-solid bg-white h-60px  form-control-lg text-dark fs-16 fw-600",
                          validation.title ? "border-danger" : "border-gray-300"
                        )}
                        name="title"
                        placeholder="Type here…"
                        value={notificationData.title}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>
                        ): void => handleInputChange(e, "title")}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={12} className="mb-6">
                  <Row className="align-items-center ">
                    <Col lg={2} className="d-flex align-items-start">
                      <label className="fs-16 fw-500 text-dark">
                        {CustomNotificationString.description}
                      </label>
                    </Col>
                    <Col lg={5}>
                      <textarea
                        className={clsx(
                          "form-control form-control-solid bg-white  form-control-lg text-dark fs-16 fw-600",
                          validation.message
                            ? "border-danger"
                            : "border-gray-300"
                        )}
                        rows={3}
                        name="message"
                        placeholder="Type here…"
                        value={notificationData.message}
                        onChange={(e: any) => {
                          handleInputChange(e, "message");
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={12} className="mb-6">
                  <Row className="align-items-center ">
                    <Col lg={2} className="d-flex align-items-start">
                      <label className="fs-16 fw-500 text-dark">
                        Pin promotion
                      </label>
                    </Col>
                    <Col lg={5} className="align-self-center d-lg-flex d-md-flex">
                      <div className="form-check form-check-custom form-check-solid me-4">
                        <input
                          className="form-check-input"
                          type="radio"
                          value="0"
                          id="pinNo"
                          data-analytics-event="custom_notification_pin_no_clicked"
                          checked={!pinPromotion}
                          onChange={(): void => {
                            setPinPromotion(false);
                            handleNotification("");
                          }}
                        />
                        <label
                          htmlFor="pinNo"
                          className="form-check-label fs-16 fw-600 text-dark"
                        >
                          No
                        </label>
                      </div>
                      <div className="form-check form-check-custom form-check-solid">
                        <input
                          className="form-check-input "
                          type="radio"
                          value="1"
                          id="pinYes"
                          data-analytics-event="custom_notification_pin_yes_clicked"
                          checked={pinPromotion}
                          onChange={(): void => {
                            setPinPromotion(true);
                            const activeCustomerOption = notificationJSON.find((n: any) => n.value === 8);
                            if (activeCustomerOption) {
                              handleNotification(activeCustomerOption);
                            }
                          }}
                        />
                        <label
                          htmlFor="pinYes"
                          className="form-check-label fs-16 fw-600 text-dark"
                        >
                          Yes
                        </label>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col lg={12} className="mb-6">
                  <Row className="align-items-center ">
                    <Col lg={2} className="d-flex align-items-start">
                      <label className="fs-16 fw-500 text-dark">
                        Send notification for:-
                      </label>
                    </Col>
                    <Col lg={5}>
                      <CustomSelectWhite
                        // border={
                        //   validation[index].primaryCategory ? '#e55451' : ''
                        // }
                        // disabled={loading}
                        // isLoading={fetchLoader}
                        options={notificationJSON}
                        loadingMessage={"Fetching Data"}
                        isMulti={false}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>
                        ): any => handleNotification(e)}
                        value={selectedNotification}
                      />
                    </Col>
                  </Row>
                </Col>
                {selectedNotification && selectedNotification.value === 8 ? (
                  <Col lg={12} className="mb-6">
                  <Row>
                    <Col lg={2}>
                      <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3 mt-lg-3">
                        Upload image
                      </label>
                    </Col>
                    <Col
                      lg={10}
                      className="d-flex"
                    >
                      <div className="d-flex flex-column">
                        <div className="upload-btn-wrapper">
                          <div className="symbol symbol-160px symbol-md-170px">
                            <img
                              className={clsx(
                                'border border-r10px bg-white',
                                validation.image
                                  ? 'border-danger'
                                  : ''
                              )}
                              src={
                                notificationData.image
                                  ? notificationData.image
                                  : UploadImage
                              }
                              alt=""
                            />
                          </div>
                          <input
                            className="w-100 h-100"
                            type="file"
                            name="myfile"
                                data-analytics-event="custom_notification_upload_image_clicked"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ): void => handleImageChange(e)}
                            disabled={loading}
                          />
                          <div className="fv-plugins-message-container">
                            <span className="text-danger fs-12 fw-bold">
                              {validation.image
                                ? 'Please upload image'
                                : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      {notificationData.image && !loading ? (
                        <label
                          className="btn btn-icon btn-circle btn-active-color-primary w-50px h-50px responsive-closed ms-2"
                          data-kt-image-input-action="change"
                          title=""
                        >
                          <img
                            src={Close}
                            onClick={(): void => handleImageDiscard()}
                            data-analytics-event="custom_notification_remove_image_clicked"
                            className="responsive-closed-icon-closed"
                            alt=""
                            height={33}
                            width={33}
                          />
                        </label>
                      ) : (
                        <></>
                      )}
                    </Col>
                  </Row>
                </Col>
                ) : (
                  <></>
                )}
                {!selectedNotification || selectedNotification.value != 7 ? (
                  <>
                    {selectedNotification?.value == 2 ||
                      selectedNotification?.value == 3 ? (
                      <></>
                    ) : (
                      <Col lg={12} className="mb-6">
                        <Row className="align-items-center ">
                          <Col lg={2} className="d-flex align-items-start">
                            <label className="fs-16 fw-500 text-dark">
                              Do you want to send notification to all customers?
                            </label>
                          </Col>
                          <Col
                            lg={5}
                            className="align-self-center d-lg-flex d-md-flex"
                          >
                            <div className="form-check form-check-custom form-check-solid me-4">
                              <input
                                className="form-check-input "
                                type="radio"
                                value="0"
                                id="singleCustomer"
                                data-analytics-event="custom_notification_send_to_all_no_clicked"
                                checked={!sendToAllCustomer}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ): void => {
                                  setSendToAllCustomer(false);
                                }}
                              />
                              <label
                                htmlFor="singleCustomer"
                                className="form-check-label fs-16 fw-600 text-dark"
                              >
                                No
                              </label>
                            </div>
                            <div className="form-check form-check-custom form-check-solid">
                              <input
                                className="form-check-input"
                                type="radio"
                                value="1"
                                id="allCustomers"
                                data-analytics-event="custom_notification_send_to_all_yes_clicked"
                                checked={sendToAllCustomer}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ): void => {
                                  setSendToAllCustomer(true);
                                }}
                              />
                              <label
                                htmlFor="allCustomers"
                                className="form-check-label fs-16 fw-600 text-dark"
                              >
                                Yes
                              </label>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    )}
                    {sendToAllCustomer === false && (
                      <Col lg={12} className="mb-6">
                        <Row className="align-items-center ">
                          <Col lg={2} className="d-flex align-items-start">
                            <label className="fs-16 fw-500 text-dark">
                              Select Customer
                            </label>
                          </Col>
                          <Col lg={5}>
                            {/* <CustomReportSelect
                        backgroundColor="#ffff"
                        minHeight="60px"
                        closeMenuOnSelect={false}
                        isSearchable={true}
                        options={customerList}
                        text={'customer selected'}
                        hideSelectedOptions={false}
                        onChange={(event: any) => {
                          handleCustomer(event);
                        }}
                        value={notificationData.buyers}
                        isMulti={
                          selectedNotification
                            ? selectedNotification.value == 2 ||
                              selectedNotification.value == 3
                              ? false
                              : true
                            : true
                        }
                      />
                       */}
                            <CustomReportSelect
                              backgroundColor="#ffff"
                              className={
                                validation.buyers
                                  ? "border border-danger rounded"
                                  : ""
                              }
                              minHeight="60px"
                              closeMenuOnSelect={false}
                              isSearchable={true}
                              options={
                                selectedNotification
                                  ? selectedNotification.value == 2 ||
                                    selectedNotification.value == 3
                                    ? customerList.filter(
                                      (val: any) => val.value !== 0
                                    )
                                    : customerList
                                  : customerList
                              }
                              isDeduct={false}
                              text={"customer selected"}
                              hideSelectedOptions={false}
                              onChange={(event: any) => {
                                handleCustomer(event);
                              }}
                              isLoading={customerLoading}
                              // isDisabled={customerLoading}
                              value={
                                notificationData.buyers.length
                                  ? notificationData.buyers
                                  : []
                              }
                              defaultValue={
                                notificationData.buyers.length
                                  ? notificationData.buyers
                                  : []
                              }
                              isMulti={
                                selectedNotification
                                  ? selectedNotification.value == 2 ||
                                    selectedNotification.value == 3
                                    ? false
                                    : true
                                  : true
                              }
                              filterOption={(option: any, rawInput: string) => true}
                              onMenuScrollToBottom={onMenuScrollToBottom}
                              onMenuClose={() => {
                                onMenuClose();
                              }}
                              onMenuOpen={() => {
                                onMenuOpen();
                              }}
                              onInputChange={(
                                newValue: any,
                                { action }: any
                              ) => {
                                if (action === "input-change") {
                                  handleSelectInputChange(newValue);
                                }
                              }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    )}
                  </>
                ) : (
                  <></>
                )}
                {selectedNotification &&
                  selectedNotification.value != 1 &&
                  selectedNotification.value != 3 &&
                  (productsList.length || productLoading) ? (
                  <Col lg={12} className="mb-6">
                    <Row className="align-items-center ">
                      <Col lg={2} className="d-flex align-items-start">
                        <label className="fs-16 fw-500 text-dark">
                          Select Product
                        </label>
                      </Col>
                      <Col lg={5}>
                        <CustomReportSelect
                          backgroundColor="#ffff"
                          minHeight="60px"
                          closeMenuOnSelect={false}
                          isSearchable={true}
                          options={productsList}
                          isLoading={productLoading}
                          text={"products selected"}
                          hideSelectedOptions={false}
                          onChange={(event: any) => {
                            handleProduct(event);
                          }}
                          value={notificationData.variants}
                          isMulti={true}
                        />
                      </Col>
                    </Row>
                  </Col>
                ) : (
                  <></>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Button className="uw-btn uw-btn-primary"
            variant="primary"
            onClick={handleSubmit}
            data-analytics-event="custom_notification_submit_clicked"
            disabled={loading || hasViewPermission}
          >
            {loading && (
              <span
                className="indicator-progress fs-16 fw-bold"
                style={{ display: "block" }}
              >
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
            {!loading && <span className="indicator-label">Submit</span>}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default CustomNotification;
